package com.milta.web

import kotlinx.coroutines.await
import kotlin.js.Promise

/** Thin interop surface over the real mermaid.js library — self-hosted as a plain <script> tag
 * (webApp/src/jsMain/resources/mermaid.min.js, loaded before webApp.js in index.html) rather than
 * an npm/webpack dependency: mermaid is ESM-only (no CommonJS entry), which @JsModule/@JsNonModule
 * interop cannot bind against. The UMD build assigns window.mermaid as a real global when no
 * module system is present at parse time, so plain dynamic access works instead. A hand-rolled
 * CSS-box flowchart cannot visually match an actual rendered graph (arrow routing, alignment), so
 * this renders the genuine thing to an SVG string. */
private val mermaidGlobal: dynamic get() = js("window.mermaid")

private var mermaidInitialized = false

/** Renders a Mermaid diagram definition to an SVG markup string. [elementId] only needs to be
 * unique per concurrent render call (mermaid uses it internally, nothing is actually inserted
 * into the DOM at that id by this call). */
suspend fun renderMermaidToSvg(definition: String, elementId: String): String {
    if (!mermaidInitialized) {
        mermaidGlobal.initialize(
            js("""({ startOnLoad: false, theme: 'neutral', securityLevel: 'strict', flowchart: { htmlLabels: false } })"""),
        )
        mermaidInitialized = true
    }
    val result = (mermaidGlobal.render(elementId, definition) as Promise<dynamic>).await()
    return result.svg as String
}
